/* eslint-disable react/no-array-index-key */
/**
 * Appetite Settings › Response Status (ID170)
 * Configurable list of "Response Status" options shown in the Watchlist Risk scenario dropdown.
 * Admin-editable: add / rename / reorder / remove. Defaults to Prepared and Tested /
 * Partially Prepared / Unprepared until customised.
 */
import React, { useEffect, useState, useCallback, useRef } from "react";
import { Box, TextField, IconButton, Tooltip, Alert, CircularProgress } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

import DashboardLayout from "pages/LayoutContainers/DashboardLayout";
import DashboardNavbar from "pages/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import useFetchWithMsal from "hooks/useFetchWithMsal";
import { protectedResources, siteData } from "authConfig";
import { useCurrentUser } from "context/UserContext";

const API = `${protectedResources.apiTodoList.endpoint}/response-status/config`;

function newId() {
  return `rs_${Math.random().toString(36).slice(2, 10)}`;
}

export default function ResponseStatus() {
  const { execute } = useFetchWithMsal({ scopes: [siteData?.apiScopes] });
  const executeRef = useRef(execute);
  useEffect(() => {
    executeRef.current = execute;
  });

  const { isRiskAdmin } = useCurrentUser();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [options, setOptions] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await executeRef.current("GET", API);
      const data = res?.data ?? res;
      const opts = Array.isArray(data?.options) ? data.options : [];
      setOptions(opts.map((o) => ({ id: o.id || newId(), label: o.label || "" })));
    } catch {
      setError("Could not load the Response Status config.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const updateLabel = (idx, value) =>
    setOptions((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], label: value };
      return next;
    });

  const addOption = () => setOptions((prev) => [...prev, { id: newId(), label: "" }]);

  const removeOption = (idx) => setOptions((prev) => prev.filter((_, i) => i !== idx));

  const move = (idx, dir) =>
    setOptions((prev) => {
      const next = [...prev];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });

  const handleSave = async () => {
    setError(null);
    setSuccess(false);
    const cleaned = options.map((o) => ({ id: o.id, label: (o.label || "").trim() }));
    if (cleaned.some((o) => !o.label)) {
      setError("Every option needs a name (or remove the empty rows).");
      return;
    }
    if (cleaned.length === 0) {
      setError("Add at least one response status option.");
      return;
    }
    setSaving(true);
    try {
      const res = await executeRef.current("PUT", API, { options: cleaned });
      if (res && res.success === false) {
        setError(res.message || "Could not save. Please try again.");
      } else {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      }
    } catch {
      setError("Could not save. Please try again.");
    }
    setSaving(false);
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox px={3} pt={6} display="flex" justifyContent="center">
          <CircularProgress sx={{ color: "#52009b" }} />
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox px={3} pt={2} pb={4}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <MDTypography variant="h5" fontWeight="medium">
            Response Status
          </MDTypography>
          {isRiskAdmin && (
            <MDBox display="flex" gap={1}>
              {/* Discard — revert any unsaved edits back to the last saved list. */}
              <MDButton
                variant="outlined"
                onClick={load}
                disabled={saving}
                sx={{
                  color: "#52009b",
                  borderColor: "#52009b",
                  textTransform: "none",
                  "&:hover": { borderColor: "#3a0070", backgroundColor: "rgba(82,0,155,0.06)" },
                }}
              >
                Discard
              </MDButton>
              <MDButton
                variant="contained"
                onClick={handleSave}
                disabled={saving}
                sx={{
                  color: "#f7f4fb",
                  backgroundColor: "#52009b",
                  textTransform: "none",
                  "&:hover": { backgroundColor: "#3a0070" },
                }}
              >
                {saving ? "Saving…" : "Save"}
              </MDButton>
            </MDBox>
          )}
        </MDBox>

        <MDTypography variant="body2" color="text" sx={{ mb: 2, maxWidth: 640 }}>
          These options appear in the &quot;Response Status&quot; dropdown on each Watchlist Risk
          scenario. {isRiskAdmin ? "Add, rename, reorder or remove them below." : ""}
        </MDTypography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Saved successfully.
          </Alert>
        )}

        <Box sx={{ maxWidth: 560 }}>
          {options.map((o, idx) => (
            <Box key={o.id} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
              <TextField
                size="small"
                fullWidth
                value={o.label}
                placeholder={`Option ${idx + 1}`}
                onChange={(e) => updateLabel(idx, e.target.value)}
                disabled={!isRiskAdmin}
              />
              {isRiskAdmin && (
                <>
                  <Tooltip title="Move up">
                    <span>
                      <IconButton size="small" disabled={idx === 0} onClick={() => move(idx, -1)}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move down">
                    <span>
                      <IconButton
                        size="small"
                        disabled={idx === options.length - 1}
                        onClick={() => move(idx, 1)}
                      >
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove">
                    <IconButton size="small" color="error" onClick={() => removeOption(idx)}>
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )}
            </Box>
          ))}

          {options.length === 0 && (
            <MDTypography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              No options yet.
            </MDTypography>
          )}

          {isRiskAdmin && (
            <MDButton
              variant="outlined"
              onClick={addOption}
              startIcon={<AddIcon />}
              sx={{
                mt: 1,
                color: "#52009b",
                borderColor: "#52009b",
                textTransform: "none",
                "&:hover": { borderColor: "#3a0070", bgcolor: "rgba(82,0,155,0.06)" },
              }}
            >
              Add option
            </MDButton>
          )}
        </Box>
      </MDBox>
    </DashboardLayout>
  );
}
